import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import SurveyBuilder from './SurveyBuilder';
import DynamicSurveyAnalytics from './DynamicSurveyAnalytics';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SurveyManagement = () => {
    const [surveys, setSurveys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('list'); // list | builder | responses | analytics
    const [editingSurveyId, setEditingSurveyId] = useState(null);
    const [selectedSurvey, setSelectedSurvey] = useState(null);

    // Responses state
    const [submissions, setSubmissions] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
    const [searchTerm, setSearchTerm] = useState('');
    const [responseFilters, setResponseFilters] = useState({ startDate: '', endDate: '' });
    const [selectedSubmission, setSelectedSubmission] = useState(null);

    const headers = {
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        'Content-Type': 'application/json'
    };

    useEffect(() => {
        if (view === 'list') fetchSurveys();
    }, [view]);

    useEffect(() => {
        if (selectedSurvey && view === 'responses') fetchSubmissions();
    }, [selectedSurvey, view, pagination.page, searchTerm, responseFilters.startDate, responseFilters.endDate]);

    const fetchSurveys = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}/survey/manage`, { headers });
            if (res.data.success) setSurveys(res.data.data.surveys);
        } catch (err) { toast.error('Failed to load surveys'); }
        finally { setLoading(false); }
    };

    const fetchSubmissions = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({ page: pagination.page, limit: pagination.limit });
            if (searchTerm) params.append('search', searchTerm);
            if (responseFilters.startDate) params.append('startDate', responseFilters.startDate);
            if (responseFilters.endDate) params.append('endDate', responseFilters.endDate);
            const res = await axios.get(`${API_URL}/survey/manage/${selectedSurvey.id}/submissions?${params}`, { headers });
            if (res.data.success) {
                setSubmissions(res.data.data.submissions);
                setPagination(p => ({ ...p, total: res.data.data.total || 0, totalPages: res.data.data.totalPages || 0, ...res.data.data.pagination }));
            }
        } catch (err) { toast.error('Failed to load submissions'); }
        finally { setLoading(false); }
    };

    const handleCreate = () => { setEditingSurveyId('new'); setView('builder'); };
    const handleEdit = (id) => { setEditingSurveyId(id); setView('builder'); };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this survey and all its responses? This cannot be undone.')) return;
        try {
            await axios.delete(`${API_URL}/survey/manage/${id}`, { headers });
            toast.success('Survey deleted');
            fetchSurveys();
        } catch (err) { toast.error('Delete failed'); }
    };

    const handleDuplicate = async (id) => {
        try {
            await axios.post(`${API_URL}/survey/manage/${id}/duplicate`, {}, { headers });
            toast.success('Survey duplicated');
            fetchSurveys();
        } catch (err) { toast.error('Duplicate failed'); }
    };

    const handleToggleStatus = async (id, newStatus) => {
        try {
            await axios.put(`${API_URL}/survey/manage/${id}/status`, { status: newStatus }, { headers });
            toast.success(`Survey set to ${newStatus}`);
            fetchSurveys();
        } catch (err) { toast.error('Status update failed'); }
    };

    const handleViewResponses = (survey) => { setSelectedSurvey(survey); setView('responses'); };
    const handleViewAnalytics = (survey) => { setSelectedSurvey(survey); setView('analytics'); };
    const handleBackToList = () => {
        setView('list');
        setSelectedSurvey(null);
        setEditingSurveyId(null);
        setSelectedSubmission(null);
        setSearchTerm('');
        setResponseFilters({ startDate: '', endDate: '' });
        setPagination(p => ({ ...p, page: 1 }));
    };

    const handleExport = async (surveyId) => {
        try {
            const res = await axios.get(`${API_URL}/survey/manage/${surveyId}/export`, { headers, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url; link.setAttribute('download', `survey-${surveyId}-responses.csv`);
            document.body.appendChild(link); link.click(); link.remove();
            toast.success('CSV exported!');
        } catch (err) { toast.error('Export failed'); }
    };

    const handleDeleteSubmission = async (id) => {
        if (!window.confirm('Delete this submission?')) return;
        try {
            await axios.delete(`${API_URL}/survey/manage/submissions/${id}`, { headers });
            toast.success('Submission deleted');
            fetchSubmissions();
        } catch (err) { toast.error('Delete failed'); }
    };

    const statusBadge = (status) => {
        const colors = { draft: 'bg-secondary', published: 'bg-success', closed: 'bg-danger' };
        return <span className={`badge ${colors[status] || 'bg-secondary'}`}>{status}</span>;
    };

    const getPageNumbers = () => {
        const { page, totalPages } = pagination;
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        if (page <= 4) return [1, 2, 3, 4, 5, '...', totalPages];
        if (page >= totalPages - 3) return [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
        return [1, '...', page - 1, page, page + 1, '...', totalPages];
    };

    // ===== BUILDER VIEW =====
    if (view === 'builder') {
        return (
            <SurveyBuilder
                surveyId={editingSurveyId}
                onBack={handleBackToList}
                onSaved={(id) => { setEditingSurveyId(id); }}
            />
        );
    }

    // ===== RESPONSES VIEW =====
    if (view === 'responses' && selectedSurvey) {
        return (
            <div>
                <div className="d-flex align-items-center mb-4">
                    <button className="btn btn-outline-secondary btn-sm me-3" onClick={handleBackToList}><i className="bi bi-arrow-left"></i> Back</button>
                    <div>
                        <h4 className="mb-0 fw-bold">{selectedSurvey.title}</h4>
                        <small className="text-muted">Submissions</small>
                    </div>
                    <button className="btn btn-sm btn-outline-primary ms-auto" onClick={() => handleExport(selectedSurvey.id)}>
                        <i className="bi bi-download me-1"></i>Export CSV
                    </button>
                </div>

                <div className="row g-3 mb-3">
                    <div className="col-md-4">
                        <input type="text" className="form-control form-control-sm" placeholder="Search submissions..."
                            value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPagination(p => ({ ...p, page: 1 })); }} />
                    </div>
                    <div className="col-md-3">
                        <input type="date" className="form-control form-control-sm" placeholder="Start date"
                            value={responseFilters.startDate} onChange={e => { setResponseFilters(f => ({ ...f, startDate: e.target.value })); setPagination(p => ({ ...p, page: 1 })); }} />
                    </div>
                    <div className="col-md-3">
                        <input type="date" className="form-control form-control-sm" placeholder="End date"
                            value={responseFilters.endDate} onChange={e => { setResponseFilters(f => ({ ...f, endDate: e.target.value })); setPagination(p => ({ ...p, page: 1 })); }} />
                    </div>
                    <div className="col-md-2">
                        <button className="btn btn-outline-secondary btn-sm w-100" onClick={() => { setSearchTerm(''); setResponseFilters({ startDate: '', endDate: '' }); setPagination(p => ({ ...p, page: 1 })); }}>
                            <i className="bi bi-x-circle me-1"></i>Clear
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
                ) : submissions.length === 0 ? (
                    <div className="text-center py-5 text-muted"><i className="bi bi-inbox fs-1 d-block mb-2"></i>No submissions yet.</div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead className="table-light">
                                    <tr><th>#</th><th>Email</th><th>Submitted</th><th>Actions</th></tr>
                                </thead>
                                <tbody>
                                    {submissions.map(s => (
                                        <tr key={s.id}>
                                            <td>{s.id}</td>
                                            <td className="small">{s.email || 'N/A'}</td>
                                            <td className="small">{new Date(s.submitted_at).toLocaleString()}</td>
                                            <td>
                                                <button className="btn btn-sm btn-outline-primary me-1" onClick={() => setSelectedSubmission(s)}><i className="bi bi-eye"></i></button>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteSubmission(s.id)}><i className="bi bi-trash"></i></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {pagination.totalPages > 1 && (
                            <nav><ul className="pagination pagination-sm justify-content-center flex-wrap">
                                <li className={`page-item ${pagination.page <= 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>Previous</button>
                                </li>
                                {getPageNumbers().map((page, idx) => (
                                    <li key={`${page}-${idx}`} className={`page-item ${pagination.page === page ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                                        {page === '...' ? <span className="page-link">...</span> : (
                                            <button className="page-link" onClick={() => setPagination(p => ({ ...p, page }))}>{page}</button>
                                        )}
                                    </li>
                                ))}
                                <li className={`page-item ${pagination.page >= pagination.totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>Next</button>
                                </li>
                            </ul></nav>
                        )}
                    </>
                )}

                {/* Submission Detail Modal */}
                {selectedSubmission && (
                    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setSelectedSubmission(null)}>
                        <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={e => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header"><h5 className="modal-title">Submission #{selectedSubmission.id}</h5><button className="btn-close" onClick={() => setSelectedSubmission(null)}></button></div>
                                <div className="modal-body">
                                    {selectedSubmission.answers && Object.entries(selectedSubmission.answers).map(([key, val]) => (
                                        <div key={key} className="mb-2">
                                            <strong className="small text-muted">{key}:</strong>
                                            <p className="mb-0">{Array.isArray(val) ? val.join(', ') : String(val || 'N/A')}</p>
                                        </div>
                                    ))}
                                    {selectedSubmission.email && <div className="mb-2"><strong className="small text-muted">Email:</strong><p className="mb-0">{selectedSubmission.email}</p></div>}
                                    <div className="mb-2"><strong className="small text-muted">Submitted:</strong><p className="mb-0">{new Date(selectedSubmission.submitted_at).toLocaleString()}</p></div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ===== ANALYTICS VIEW =====
    if (view === 'analytics' && selectedSurvey) {
        return (
            <DynamicSurveyAnalytics
                surveyId={selectedSurvey.id}
                surveyTitle={selectedSurvey.title}
                onBack={handleBackToList}
            />
        );
    }

    // ===== LIST VIEW =====
    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="h4 fw-bold mb-0"><i className="bi bi-clipboard-data me-2" style={{ color: '#6B8E23' }}></i>Survey Management</h2>
                <button className="btn btn-primary" onClick={handleCreate} style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>
                    <i className="bi bi-plus-lg me-1"></i>Create New Survey
                </button>
            </div>

            {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
            ) : surveys.length === 0 ? (
                <div className="text-center py-5">
                    <i className="bi bi-clipboard-plus fs-1 text-muted d-block mb-2"></i>
                    <p className="text-muted">No surveys yet. Create your first survey to get started.</p>
                    <button className="btn btn-primary" onClick={handleCreate} style={{ backgroundColor: '#6B8E23', borderColor: '#6B8E23' }}>
                        <i className="bi bi-plus-lg me-1"></i>Create Survey
                    </button>
                </div>
            ) : (
                <div className="row g-3">
                    {surveys.map(s => (
                        <div key={s.id} className="col-md-6">
                            <div className="card border-0 shadow-sm h-100">
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <h5 className="fw-bold mb-0">{s.title}</h5>
                                        {statusBadge(s.status)}
                                    </div>
                                    <p className="text-muted small mb-2">{s.description || 'No description'}</p>
                                    <div className="d-flex gap-3 mb-3">
                                        <span className="small text-muted"><i className="bi bi-question-circle me-1"></i>{(typeof s.questions === 'string' ? JSON.parse(s.questions) : s.questions || []).length} questions</span>
                                        <span className="small text-muted"><i className="bi bi-people me-1"></i>{s.submission_count || 0} responses</span>
                                        <span className="small text-muted"><i className="bi bi-calendar me-1"></i>{new Date(s.created_at).toLocaleDateString()}</span>
                                    </div>
                                    <div className="small text-muted mb-3">
                                        <i className="bi bi-link-45deg me-1"></i>/survey/{s.slug}
                                    </div>
                                    <div className="d-flex flex-wrap gap-1">
                                        <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(s.id)}><i className="bi bi-pencil me-1"></i>Edit</button>
                                        <button className="btn btn-sm btn-outline-info" onClick={() => handleViewResponses(s)}><i className="bi bi-list-ul me-1"></i>Responses</button>
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleViewAnalytics(s)}><i className="bi bi-bar-chart me-1"></i>Analytics</button>
                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => handleExport(s.id)}><i className="bi bi-download me-1"></i>Export</button>
                                        <div className="btn-group">
                                            <button className="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">More</button>
                                            <ul className="dropdown-menu">
                                                {s.status !== 'published' && <li><button className="dropdown-item" onClick={() => handleToggleStatus(s.id, 'published')}><i className="bi bi-check-circle me-2"></i>Publish</button></li>}
                                                {s.status !== 'closed' && <li><button className="dropdown-item" onClick={() => handleToggleStatus(s.id, 'closed')}><i className="bi bi-x-circle me-2"></i>Close</button></li>}
                                                {s.status !== 'draft' && <li><button className="dropdown-item" onClick={() => handleToggleStatus(s.id, 'draft')}><i className="bi bi-file-earmark me-2"></i>Set Draft</button></li>}
                                                <li><hr className="dropdown-divider" /></li>
                                                <li><button className="dropdown-item" onClick={() => handleDuplicate(s.id)}><i className="bi bi-copy me-2"></i>Duplicate</button></li>
                                                <li><button className="dropdown-item text-danger" onClick={() => handleDelete(s.id)}><i className="bi bi-trash me-2"></i>Delete</button></li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SurveyManagement;
